/**
 * SmartMergeUpload — Phase 3.46
 *
 * Three-step dialog for uploading new data to an EXISTING program:
 *
 *   1. Pick file         — single CSV upload.
 *   2. Review            — backend returns two buckets:
 *        • Matched (auto-mapped to existing indicators, no input needed)
 *        • New indicators to add (with Select All + per-row checkboxes)
 *   3. Confirm           — single-line pre-commit summary
 *        "Updating X existing indicators. Adding Y new indicators. Proceed?"
 *
 * On success the parent reloads program data + we redirect the user back
 * to the program detail page (or stay, parent's call via `onCommitted`).
 */
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Sparkles, CheckCircle2, AlertCircle, ArrowLeft } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import api from "../../lib/api";

export default function SmartMergeUpload({ pid, open, onClose, onCommitted }) {
  const [step, setStep] = useState("pick"); // pick | review | committing
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [file, setFile] = useState(null);
  const [matched, setMatched] = useState([]);
  const [newSuggestions, setNewSuggestions] = useState([]);
  const [ignored, setIgnored] = useState([]);
  const [periodColumn, setPeriodColumn] = useState(null);
  const [totals, setTotals] = useState({ headers: 0, rows: 0 });
  const fileRef = useRef(null);

  const reset = () => {
    setStep("pick"); setFile(null); setMatched([]); setNewSuggestions([]);
    setIgnored([]); setPeriodColumn(null); setTotals({ headers: 0, rows: 0 });
  };
  const close = () => { reset(); onClose(); };

  const uploadForPreview = async (f) => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post(
        `/programs/${pid}/indicators/smart-merge-preview`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setFile(f);
      setMatched(res.data.matched || []);
      setNewSuggestions(res.data.new_suggestions || []);
      setIgnored(res.data.ignored || []);
      setPeriodColumn(res.data.period_column);
      setTotals({ headers: res.data.total_headers || 0, rows: res.data.total_rows || 0 });
      setStep("review");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't analyse the file");
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggleNew = (idx) => {
    setNewSuggestions((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], include: !next[idx].include && next[idx].status === "valid" };
      return next;
    });
  };

  const includedNewCount = newSuggestions.filter((r) => r.include && r.status === "valid").length;
  const allValidSelected = newSuggestions.length > 0
    && newSuggestions.every((r) => r.status !== "valid" || r.include);
  const toggleAllNew = () => {
    setNewSuggestions((prev) => prev.map((r) => ({
      ...r, include: r.status === "valid" ? !allValidSelected : false,
    })));
  };

  const updateNewField = (idx, field, value) => {
    setNewSuggestions((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], parsed: { ...next[idx].parsed, [field]: value } };
      return next;
    });
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("payload_json", JSON.stringify({
        matched,
        new_indicators: newSuggestions,
        period_column: periodColumn,
        filename: file.name,
      }));
      const res = await api.post(
        `/programs/${pid}/indicators/smart-merge-commit`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const { added_indicators, updated_indicators, rows_ingested } = res.data;
      toast.success(
        `Smart Merge complete — ${updated_indicators} indicator(s) updated, ${added_indicators} added, ${rows_ingested} rows ingested.`,
      );
      onCommitted && onCommitted(res.data);
      close();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Smart Merge failed");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="smart-merge-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-600" /> Upload new data — Smart Merge
          </DialogTitle>
          <DialogDescription>
            We&apos;ll match your columns to existing indicators automatically. Any unknown headers can be added as new indicators with one click.
          </DialogDescription>
        </DialogHeader>

        {step === "pick" && (
          <div className="mt-2" data-testid="smart-merge-pick">
            <div className="border-2 border-dashed border-emerald-300 bg-emerald-50/30 rounded-md p-8 text-center">
              <Upload className="w-7 h-7 text-emerald-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-slate-900">Upload a CSV with your latest data</p>
              <p className="text-xs text-slate-600 mt-1 max-w-md mx-auto">
                Existing indicators with matching column names get refreshed automatically. Brand-new headers become &quot;New Indicators to Add&quot; in the next step.
              </p>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadForPreview(e.target.files[0])}
                data-testid="smart-merge-file-input"
              />
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={loading}
                className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-medium mt-4"
                data-testid="smart-merge-pick-file-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                {loading ? "Analyzing…" : "Choose file"}
              </Button>
            </div>
          </div>
        )}

        {step === "review" && (
          <div className="mt-3 space-y-4" data-testid="smart-merge-review">
            <button
              type="button"
              onClick={reset}
              className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
              data-testid="smart-merge-back"
            >
              <ArrowLeft className="w-3 h-3" /> Pick another file
            </button>

            {/* Summary chips */}
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <span className="bg-slate-900 text-white px-2 py-1 rounded-sm font-mono">
                {totals.headers} column{totals.headers === 1 ? "" : "s"} · {totals.rows} row{totals.rows === 1 ? "" : "s"}
              </span>
              <span className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-2 py-1 rounded-sm">
                {matched.length} matched
              </span>
              <span className="bg-amber-50 border border-amber-200 text-amber-800 px-2 py-1 rounded-sm">
                {newSuggestions.length} new
              </span>
              <span className="bg-slate-50 border border-slate-200 text-slate-600 px-2 py-1 rounded-sm">
                {ignored.length} ignored
              </span>
              {periodColumn && (
                <span className="bg-sky-50 border border-sky-200 text-sky-800 px-2 py-1 rounded-sm">
                  Period: <span className="font-mono">{periodColumn}</span>
                </span>
              )}
            </div>

            {/* Matched bucket */}
            {matched.length > 0 && (
              <section data-testid="smart-merge-matched">
                <h4 className="font-heading text-sm font-semibold mb-2 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  Updating {matched.length} existing indicator{matched.length === 1 ? "" : "s"}
                </h4>
                <p className="text-xs text-slate-500 mb-2">
                  These columns map to indicators you&apos;ve already defined. Their data will be refreshed in place.
                </p>
                <ul className="border border-slate-200 rounded-md divide-y divide-slate-100 text-xs max-h-44 overflow-y-auto">
                  {matched.map((m) => (
                    <li key={m.column} className="px-3 py-2 flex items-center gap-3" data-testid={`matched-row-${m.column}`}>
                      <span className="font-mono text-slate-700 truncate flex-1">{m.column}</span>
                      <span className="text-slate-400">→</span>
                      <span className="font-mono text-emerald-700 truncate flex-1">{m.indicator_code || m.indicator_name}</span>
                      <span className="text-[10px] uppercase tracking-wider text-slate-500">{m.match_type}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* New indicators to add */}
            {newSuggestions.length > 0 && (
              <section data-testid="smart-merge-new">
                <div className="flex items-center justify-between mb-2 gap-2">
                  <h4 className="font-heading text-sm font-semibold flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-600" />
                    New Indicators to Add ({newSuggestions.length})
                  </h4>
                  <label className="inline-flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer" data-testid="smart-merge-select-all-label">
                    <input
                      type="checkbox"
                      checked={allValidSelected}
                      onChange={toggleAllNew}
                      className="accent-emerald-500"
                      data-testid="smart-merge-select-all"
                    />
                    Select all
                  </label>
                </div>
                <div className="border border-slate-200 rounded-md max-h-72 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 z-10">
                      <tr>
                        <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider text-slate-500 w-8"></th>
                        <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider text-slate-500">Column</th>
                        <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider text-slate-500">Code</th>
                        <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider text-slate-500">Name</th>
                        <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider text-slate-500">Unit</th>
                        <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider text-slate-500">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newSuggestions.map((r, idx) => (
                        <tr
                          key={`${r.source_column || r.row_num}`}
                          className={`border-b border-slate-100 ${r.status === "error" ? "bg-rose-50/30" : ""}`}
                          data-testid={`new-row-${idx}`}
                        >
                          <td className="px-2 py-2 align-top">
                            <input
                              type="checkbox"
                              checked={!!r.include}
                              disabled={r.status === "error"}
                              onChange={() => toggleNew(idx)}
                              className="accent-emerald-500"
                              data-testid={`new-row-include-${idx}`}
                            />
                          </td>
                          <td className="px-2 py-2 align-top font-mono text-slate-500 truncate max-w-[160px]" title={r.source_column}>
                            {r.source_column}
                          </td>
                          <td className="px-2 py-2 align-top">
                            <Input
                              value={r.parsed.code}
                              onChange={(e) => updateNewField(idx, "code", e.target.value)}
                              className="text-xs h-8 font-mono"
                              data-testid={`new-row-code-${idx}`}
                            />
                          </td>
                          <td className="px-2 py-2 align-top">
                            <Input
                              value={r.parsed.name}
                              onChange={(e) => updateNewField(idx, "name", e.target.value)}
                              className="text-xs h-8"
                              data-testid={`new-row-name-${idx}`}
                            />
                          </td>
                          <td className="px-2 py-2 align-top">
                            <Input
                              value={r.parsed.unit}
                              onChange={(e) => updateNewField(idx, "unit", e.target.value)}
                              className="text-xs h-8 w-24"
                            />
                          </td>
                          <td className="px-2 py-2 align-top">
                            <select
                              value={r.parsed.type}
                              onChange={(e) => updateNewField(idx, "type", e.target.value)}
                              className="text-xs h-8 border border-slate-200 rounded-sm px-2"
                            >
                              {["output", "outcome", "impact", "activity", "input"].map((t) => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {newSuggestions.some((r) => r.status === "error") && (
                    <div className="px-3 py-2 bg-rose-50 border-t border-rose-200 text-xs text-rose-700">
                      {newSuggestions.filter((r) => r.status === "error").map((r) => (
                        <p key={r.row_num} className="flex items-center gap-1.5">
                          <AlertCircle className="w-3 h-3" /> {r.source_column}: {r.errors.join("; ")}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Pre-commit summary + commit CTA */}
            <div
              className="border-t border-slate-100 pt-3 flex items-center justify-between gap-3 flex-wrap"
              data-testid="smart-merge-summary"
            >
              <p className="text-sm text-slate-700">
                <span className="font-medium">Updating {matched.length}</span> existing indicator{matched.length === 1 ? "" : "s"}.{" "}
                <span className="font-medium">Adding {includedNewCount}</span> new indicator{includedNewCount === 1 ? "" : "s"}.
                {" "}Proceed?
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={close} disabled={committing}>Cancel</Button>
                <Button
                  onClick={commit}
                  disabled={committing || (matched.length === 0 && includedNewCount === 0)}
                  className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-medium"
                  data-testid="smart-merge-commit-btn"
                >
                  {committing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                  {committing ? "Merging…" : "Run Smart Merge"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
